import { useCallback } from 'react';
import { log } from 'log';
import { prop } from 'ramda';
import { useDispatch } from 'react-redux';
import { useNavigate, useRemoveQueryParams } from '@theme/js/hooks/navigate';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';
import { useGetErrorMessage } from '@core/js/hooks/server/hooks';
import { useCheckUserEmailVerificationStatus } from 'server';
import { LAYER } from '@core/js/components/common/PopupManager/layers';
import { showCommonPopupAction } from '@theme/js/components/common/popups/CommonPopup';

export const useHandleEmailVerification = () => {
    const tkPrefix = 'playtech.mobile.system.emailVerification.status.';
    const dispatch = useDispatch();
    const navigateWithReplace = useNavigate({ replace: true });
    const removeQueryParams = useRemoveQueryParams(); /* Lugano changes */
    const getErrorMessage = useGetErrorMessage({ tkPrefix });
    const checkUserEmailVerificationStatus = useCheckUserEmailVerificationStatus();
    const { markEmailVerified } = useUserSelector(prop('userDetails'));
    const dynamicTags = useAppSelector(prop('dynamicTags')); /* Lugano changes */

    /* Lugano changes */
    const navigateToRootPage = useCallback(() => {
        navigateWithReplace(
            '/',
            undefined,
            { removeParams: ['emailToken'] }
        );
    }, [navigateWithReplace]);

    const showPopup = useCallback((errorMessage?: string): void => {
        dispatch(showCommonPopupAction({
            className: 'email-verification-popup', /* Lugano changes */
            title: `${tkPrefix}title.${errorMessage ? 'error' : 'success'}`,
            content: errorMessage || `${tkPrefix}success`,
            layer: LAYER.HIGH_PRIORITY,
            onClose: () => navigateToRootPage()
        }));
    }, [dispatch, navigateToRootPage]);

    const handleMissingDtag = useCallback((isError: boolean, dtagUrl: string, error?: any): void => {
        removeQueryParams(['emailToken']); /* Lugano changes */

        if (isError) {
            showPopup(getErrorMessage(error as Error));
        } else {
            showPopup();
        }
        log('error', `Missing DYNAMIC TAG: ${dtagUrl} . Please set it up in CMS!`);
    }, [removeQueryParams, showPopup, getErrorMessage]);

    const handleDtagNavigation = useCallback((isError: boolean, dtagUrlKey: string, error?: any): void => {
        const dtagUrl = dynamicTags?.[dtagUrlKey];

        if (dtagUrl) {
            navigateWithReplace(dtagUrl, undefined, { removeParams: ['emailToken'] });
        } else {
            handleMissingDtag(isError, dtagUrlKey, error);
        }
    }, [dynamicTags, navigateWithReplace, handleMissingDtag]);
    /* Lugano changes */

    return useCallback(async (token: string): Promise<void> => {
        if (Number(markEmailVerified)) {
            navigateToRootPage(); /* Lugano changes */
            return;
        }

        /* Lugano changes */
        try {
            await checkUserEmailVerificationStatus(token);
            handleDtagNavigation(false, 'DTAG_SUCCESS_EMAIL_VERIFICATION_URL');
        } catch (error) {
            handleDtagNavigation(true, 'DTAG_FAIL_EMAIL_VERIFICATION_URL', error);
        }
        /* Lugano changes */
    }, [markEmailVerified, navigateToRootPage, checkUserEmailVerificationStatus, handleDtagNavigation]);
};
